import {
  useTimeByInsightsTimeframe,
  useTimeframeNavigation,
} from '@griply/hooks';
import { fallbackTimeframe } from '@griply/types';
import { getWeekStartsOn } from '@griply/utils';
import { useUpdateInsightsTimeframe, useUserInsights } from 'features/user';
import React, { useMemo } from 'react';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';

import { InsightsTimeframeContext } from './context';

export type InsightsTimeframeProviderProps = {
  children: React.ReactNode;
};

export const InsightsTimeframeProvider: React.FC<
  InsightsTimeframeProviderProps
> = ({ children }) => {
  const user = useUser();
  const today = useToday();
  const weekStartsOn = getWeekStartsOn(user);
  const userInsights = useUserInsights();
  const setTimeframe = useUpdateInsightsTimeframe();

  const timeframe = userInsights?.timeframe ?? fallbackTimeframe;

  const { referenceDate, onPrev, onToday, onNext, isNextDisabled } =
    useTimeframeNavigation(timeframe, { today, weekStartsOn });

  const { startDate, endDate } = useTimeByInsightsTimeframe(timeframe, {
    today: referenceDate,
    weekStartsOn,
    weekEndsWithStartOfDay: true,
  });

  const value = useMemo(
    () => ({
      timeframe,
      setTimeframe,
      referenceDate,
      startDate,
      endDate,
      onPrev,
      onToday,
      onNext,
      isNextDisabled,
    }),
    [
      timeframe,
      setTimeframe,
      referenceDate,
      startDate,
      endDate,
      onPrev,
      onToday,
      onNext,
      isNextDisabled,
    ],
  );

  return (
    <InsightsTimeframeContext.Provider value={value}>
      {children}
    </InsightsTimeframeContext.Provider>
  );
};
